import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { environment } from '../environments/environment';

export interface SocketServerEvent<T = unknown> {
  event: string;
  socket_id?: string;
  message?: string;
  data?: T;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket: WebSocket | null = null;
  private readonly eventSubject = new Subject<SocketServerEvent>();
  private readonly socketIdSubject = new BehaviorSubject<string | null>(this.readStoredSocketId());

  readonly events$: Observable<SocketServerEvent> = this.eventSubject.asObservable();
  readonly socketId$: Observable<string | null> = this.socketIdSubject.asObservable();

  connect(socketUrl: string = environment.socketUrl): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const resolvedUrl = this.resolveSocketUrl(socketUrl);
    if (!resolvedUrl) {
      return;
    }

    try {
      this.socket = new WebSocket(resolvedUrl);
      this.socket.onmessage = (event) => this.handleMessage(event.data);
      this.socket.onclose = () => {
        this.socket = null;
      };
      this.socket.onerror = () => {
        this.socketIdSubject.next(this.readStoredSocketId());
      };
    } catch {
      this.socket = null;
    }
  }

  disconnect(): void {
    if (!this.socket) {
      return;
    }

    this.socket.onmessage = null;
    this.socket.onclose = null;
    this.socket.onerror = null;
    this.socket.close();
    this.socket = null;
  }

  private handleMessage(rawMessage: string): void {
    let message: SocketServerEvent | null = null;
    try {
      message = JSON.parse(rawMessage) as SocketServerEvent;
    } catch {
      return;
    }

    if (!message || typeof message.event !== 'string') {
      return;
    }

    if (message.event === 'socket_connected' && typeof message.socket_id === 'string') {
      this.storeSocketId(message.socket_id);
    }

    this.eventSubject.next(message);
  }

  private resolveSocketUrl(socketUrl: string): string | null {
    const candidate = (socketUrl ?? '').trim();
    if (!candidate) {
      return null;
    }

    if (candidate.startsWith('ws://') || candidate.startsWith('wss://')) {
      return candidate;
    }

    if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
      return candidate.replace(/^http/, 'ws');
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    if (candidate.startsWith('/')) {
      return `${protocol}//${host}${candidate}`;
    }

    return `${protocol}//${host}/${candidate}`;
  }

  private storeSocketId(socketId: string): void {
    this.socketIdSubject.next(socketId);
    try {
      sessionStorage.setItem('socket_id', socketId);
    } catch {
      // Ignore storage failures in restricted browser contexts.
    }
  }

  private readStoredSocketId(): string | null {
    try {
      return sessionStorage.getItem('socket_id');
    } catch {
      return null;
    }
  }
}
